// ===============================================
// File: src/contexts/PackagingContext.jsx
// Unified persistence (Folder OR Drive) via StorageContext.
// If no workspace linked => in-memory empty state; writes are no-ops.
// Toaster events on CRUD/saves (mudo:toast) preserved.
// ===============================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useStorage } from "./StorageContext";
import { useNotifications } from "./NotificationsContext";

/* ---------- file paths ---------- */
const FILE_ITEMS = "packaging/items.json";
const FILE_RULES = "packaging/rules.json";

const SAVE_DEBOUNCE_MS = 400;

/* ---------- helpers ---------- */
const coerceList = (v) => (Array.isArray(v) ? v : []);
const now = () => Date.now();
const safeId = () => (globalThis.crypto?.randomUUID?.() || `item-${Date.now()}-${Math.random().toString(36).slice(2,6)}`);

const stableStringify = (value) => {
  const seen = new WeakSet();
  const stringify = (v) => {
    if (v === null || v === undefined) return v;
    if (typeof v !== "object") return v;
    if (seen.has(v)) return "[Circular]";
    seen.add(v);

    if (Array.isArray(v)) return v.map(stringify);

    const out = {};
    for (const key of Object.keys(v).sort()) {
      out[key] = stringify(v[key]);
    }
    return out;
  };

  try {
    return JSON.stringify(stringify(value));
  } catch {
    return "";
  }
};

const stableHash = (value) => stableStringify(value);

/* ---------- tiny toast helper ---------- */
const toast = (message, tone = "info", ttl = 2200) => {
  try {
    window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { message, tone, ttl } }));
  } catch {}
};

/* ---------- context ---------- */
export const PackagingContext = createContext({
  items: [],
  rules: [],
  addItem: () => {},
  updateItem: () => {},
  deleteItem: () => {},
  addRule: () => {},
  updateRule: () => {},
  deleteRule: () => {},
  resolvePackagingUsage: () => ({
    perUnitCost: 0,
    perBatchCost: 0,
    totalCost: 0,
    breakdown: [],
  }),
  totalItems: 0,
  activeRulesCount: 0,
  byCategory: [],
  lastUpdated: 0,
});

// Preserve all useful UI fields so they render in the Library
const normItem = (item) => {
  const t = now();
  // NEW: Preserve existing timestamps during hydration to avoid hash changes
  const createdAt = Number(item?.createdAt) || t;
  const updatedAt = Number(item?.updatedAt) || createdAt;
  
  const normalized = {
    id: item?.id || safeId("item"),
    name: String(item?.name || "Untitled").trim(),
    category: String(item?.category || "Packaging").trim(),

    // pricing
    pricingMode: item?.pricingMode || "unit", // "unit" | "pack"
    unitPrice: item?.pricingMode === "unit" ? Number(item?.unitPrice ?? 0) || 0 : null,
    packPrice: item?.pricingMode === "pack" ? Number(item?.packPrice ?? 0) || 0 : null,
    unitsPerPack: item?.pricingMode === "pack" ? Number(item?.unitsPerPack ?? 0) || 0 : null,

    // extras used by UI
    uom: item?.uom || "each",
    yieldPerUnit: Number(item?.yieldPerUnit ?? 1) || 1,
    wastePct: Number(item?.wastePct ?? 0) || 0,
    taxFlag: item?.taxFlag || "inclusive",
    dims: item?.dims || { l: null, w: null, h: null, unit: "mm" },
    weight: item?.weight || { value: null, unit: "g" },
    supplier: String(item?.supplier || "").trim(),
    sku: String(item?.sku || "").trim(),
    link: String(item?.link || "").trim(),
    notes: String(item?.notes || ""),
    image: item?.image || null,
    lowStockLimit: item?.lowStockLimit !== undefined && item?.lowStockLimit !== null ? Number(item.lowStockLimit) : null,
    stock: item?.stock !== undefined && item?.stock !== null ? Number(item.stock) : null,

    createdAt,
    updatedAt,
  };
  
  if (import.meta?.env?.DEV) {
    console.log('[PackagingContext normItem]', {
      input: { name: item?.name, lowStockLimit: item?.lowStockLimit, stock: item?.stock },
      output: { name: normalized.name, lowStockLimit: normalized.lowStockLimit, stock: normalized.stock }
    });
  }
  
  return normalized;
};

const normRule = (rule) => {
  const t = now();
  // NEW: Preserve existing timestamps during hydration to avoid hash changes
  const createdAt = Number(rule?.createdAt) || t;
  const updatedAt = Number(rule?.updatedAt) || createdAt;
  return {
    id: rule?.id || safeId("rule"),
    itemId: rule?.itemId || "",
    active: rule?.active ?? true,
    scope: rule?.scope || "global", // "global" | "product" | "batch"
    productId: rule?.productId || "",
    batchId: rule?.batchId || "",
    appliesAs: rule?.appliesAs || "perUnit", // "perUnit" | "perBatch" | "threshold"
    qtyPerUnit: Number(rule?.qtyPerUnit ?? 0) || 0,
    qtyPerBatch: Number(rule?.qtyPerBatch ?? 0) || 0,
    threshold: rule?.threshold || null, // { minQty, step, perStepQty }
    createdAt,
    updatedAt,
  };
};

/* ---------- provider ---------- */
export function PackagingProvider({ children }) {
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = useStorage();
  const { notifyAndInbox } = useNotifications() || {};

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  const [items, setItems] = useState([]);
  const [rules, setRules] = useState([]);

  // Keep latest values handy for immediate persistence without waiting for state updates
  const itemsRef = useRef([]);
  const rulesRef = useRef([]);

  const loadedRef = useRef(false);
  const hydratingRef = useRef(false);
  const hydrationDepthRef = useRef(0); // NEW: depth counter for nested hydrations
  const previousDataRef = useRef({ items: null, rules: null });
  const defaultsInjectedItemsRef = useRef(false);
  const defaultsInjectedRulesRef = useRef(false);
  const debounceItems = useRef(0);
  const debounceRules = useRef(0);
  const lastSavedHashItemsRef = useRef(null); // NEW: hash of last saved items
  const lastSavedHashRulesRef = useRef(null); // NEW: hash of last saved rules

  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { rulesRef.current = rules; }, [rules]);

  const notifyPackaging = useCallback(
    ({ id, message, tone = "info", level = "info", actionTo, dedupeKey }) => {
      // NEW: Skip notifications during hydration
      if (hydratingRef.current) return;
      
      toast(message, tone);
      notifyAndInbox?.({
        id,
        dedupeKey: dedupeKey || `packaging:${id}`,
        createdAt: Date.now(),
        level,
        title: "Packaging",
        message,
        source: "system",
        world: "studio",
        entity: { type: "packaging" },
        action: { kind: "NAVIGATE", to: actionTo || "/dashboard/packaging/library" },
      });
    },
    [notifyAndInbox]
  );

  const itemKey = (it) => String(it?.id || it?.sku || it?.name || "");
  const ruleKey = (r) => String(r?.id || r?.itemId || "");

  /* ---- load once / re-hydrate on workspace availability ---- */
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;
    hydrationDepthRef.current++; // NEW: increment depth
    hydratingRef.current = true;

    (async () => {
      if (!hasWorkspace) {
        if (alive) { setItems([]); setRules([]); }
        loadedRef.current = true;
        return;
      }
      try {
        const [fItems, fRules] = await Promise.all([
          loadJSON(FILE_ITEMS, []),
          loadJSON(FILE_RULES, []),
        ]);
        if (import.meta?.env?.DEV) {
          console.log('[PackagingContext hydrate] Loaded from storage:', {
            itemCount: fItems?.length,
            firstItem: fItems?.[0]
          });
        }
        if (!alive) return;

        // Prime hashes so the first post-hydrate render doesn't trigger a no-op save.
        lastSavedHashItemsRef.current = stableHash(coerceList(fItems));
        lastSavedHashRulesRef.current = stableHash(coerceList(fRules));
        
        setItems(coerceList(fItems));
        setRules(coerceList(fRules));
      } catch {
        if (!alive) return;
        setItems([]); setRules([]);
      } finally {
        loadedRef.current = true;
        hydrationDepthRef.current--; // NEW: decrement depth
        hydratingRef.current = hydrationDepthRef.current > 0; // NEW: recalc hydrating flag
        
        if (import.meta?.env?.DEV && hydrationDepthRef.current === 0) {
          console.log('[PackagingContext] Hydration complete - autosave enabled');
        }
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace, loadJSON]);

  /* ---- rehydrate after Receive (vault or files) ---- */
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      hydrationDepthRef.current++;
      hydratingRef.current = true;
      try {
        const [fItems, fRules] = await Promise.all([
          loadJSON(FILE_ITEMS, []),
          loadJSON(FILE_RULES, []),
        ]);

        lastSavedHashItemsRef.current = stableHash(coerceList(fItems));
        lastSavedHashRulesRef.current = stableHash(coerceList(fRules));

        setItems(coerceList(fItems));
        setRules(coerceList(fRules));
      } catch {
        // ignore
      } finally {
        hydrationDepthRef.current--;
        hydratingRef.current = hydrationDepthRef.current > 0;
        
        if (import.meta?.env?.DEV && hydrationDepthRef.current === 0) {
          console.log('[PackagingContext] Re-hydration complete (vault/files restored)');
        }
      }
    };
    
    const onDisconnect = () => {
      setItems([]);
      setRules([]);
      loadedRef.current = false;
      lastSavedHashItemsRef.current = null;
      lastSavedHashRulesRef.current = null;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [loadJSON, hasWorkspace]);

  /* ---- persist on change (debounced, after initial load) ---- */
  /* ---- persist items whenever they change (after initial load, debounced) ---- */
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    if (hydratingRef.current) return;

    const nextHash = stableHash(items);
    if (lastSavedHashItemsRef.current && nextHash === lastSavedHashItemsRef.current) {
      // Skip save: data unchanged
      return;
    }

    clearTimeout(debounceItems.current);
    debounceItems.current = setTimeout(async () => {
      const saveHash = stableHash(items);
      if (lastSavedHashItemsRef.current && saveHash === lastSavedHashItemsRef.current) {
        // Double-check before save: data unchanged
        return;
      }

      // Set hash BEFORE saving to prevent loop if save triggers any state change
      lastSavedHashItemsRef.current = saveHash;

      if (import.meta?.env?.DEV) {
        console.log('[PackagingContext] Saving items:', { itemCount: items?.length });
      }
      try {
        await saveJSON(FILE_ITEMS, items, { reason: "packaging-items-change", immediate: true });
        // Success notification suppressed to avoid triggering loops
        if (import.meta?.env?.DEV) {
          console.log('[PackagingContext] Items saved successfully');
        }
      } catch (err) {
        // Restore hash on failure so next change triggers retry
        lastSavedHashItemsRef.current = null;
        console.error('[PackagingContext] Failed to save items:', err);
        notifyPackaging({
          id: "packaging-items-save-failed",
          message: "Failed to save packaging items",
          tone: "error",
          level: "error",
          actionTo: "/dashboard/packaging/library",
        });
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(debounceItems.current);
  }, [items, hasWorkspace, saveJSON, notifyPackaging]);

  /* ---- persist rules whenever they change (after initial load, debounced) ---- */
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    if (hydratingRef.current) return;

    const nextHash = stableHash(rules);
    if (lastSavedHashRulesRef.current && nextHash === lastSavedHashRulesRef.current) {
      // Skip save: data unchanged
      return;
    }

    clearTimeout(debounceRules.current);
    debounceRules.current = setTimeout(async () => {
      const saveHash = stableHash(rules);
      if (lastSavedHashRulesRef.current && saveHash === lastSavedHashRulesRef.current) {
        // Double-check before save: data unchanged
        return;
      }

      // Set hash BEFORE saving to prevent loop
      lastSavedHashRulesRef.current = saveHash;

      try {
        await saveJSON(FILE_RULES, rules, { reason: "packaging-rules-change", immediate: true });
        // Silent success to avoid loops
        if (import.meta?.env?.DEV) {
          console.log('[PackagingContext] Rules saved successfully');
        }
      } catch (err) {
        // Restore hash on failure so next change triggers retry
        lastSavedHashRulesRef.current = null;
        console.error('[PackagingContext] Failed to save rules:', err);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(debounceRules.current);
  }, [rules, hasWorkspace, saveJSON]);

  // Immediate, silent persist helper used for destructive actions (e.g., delete)
  const persistPackagingImmediate = useCallback(
    async (nextItems, nextRules) => {
      if (!hasWorkspace || !loadedRef.current || hydratingRef.current) return;
      try {
        await Promise.all([
          saveJSON(FILE_ITEMS, nextItems, { reason: "packaging-immediate", immediate: true }),
          saveJSON(FILE_RULES, nextRules, { reason: "packaging-immediate", immediate: true }),
        ]);
        lastSavedHashItemsRef.current = stableHash(nextItems);
        lastSavedHashRulesRef.current = stableHash(nextRules);
      } catch (err) {
        console.error("[Packaging] immediate save failed", err);
      }
    },
    [hasWorkspace, saveJSON]
  );

  /* ---- price helper ---- */
  const pricePerUnit = (item) => {
    const wasteMult = 1 + (Number(item.wastePct || 0) / 100);
    let unitBase = 0;
    if (item.pricingMode === "unit" && item.unitPrice != null) {
      unitBase = Number(item.unitPrice);
    } else if (
      item.pricingMode === "pack" &&
      item.packPrice != null &&
      item.unitsPerPack
    ) {
      unitBase = Number(item.packPrice) / Number(item.unitsPerPack || 1);
    }
    return unitBase * wasteMult;
  };

  /* ---- CRUD: items ---- */
  const addItem = useCallback((item) => {
    const id = item?.id || safeId();
    const newItem = {
      ...item,
      id,
      createdAt: item?.createdAt ?? now(),
      updatedAt: now(),
    };
    if (import.meta?.env?.DEV) {
      console.log('[PackagingContext addItem] Adding item:', { input: item, newItem });
    }
    setItems((prev) => [newItem, ...coerceList(prev)]);
    notifyPackaging({
      id: `packaging-item-added-${id}`,
      message: "Item added",
      tone: "success",
      level: "success",
      actionTo: "/dashboard/packaging/library",
    });
  }, [notifyPackaging]);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) =>
      coerceList(prev).map((it) =>
        String(it.id) === String(id)
          ? { ...it, ...patch, id: it.id, createdAt: it.createdAt, updatedAt: now() }
          : it
      )
    );
    notifyPackaging({
      id: `packaging-item-updated-${id}`,
      message: "Item updated",
      tone: "success",
      level: "success",
      actionTo: "/dashboard/packaging/library",
    });
  }, [notifyPackaging]);

  const deleteItem = useCallback((id) => {
    const nextItems = (itemsRef.current || []).filter((it) => String(it.id) !== String(id));
    const nextRules = (rulesRef.current || []).filter((r) => String(r.itemId) !== String(id));

    setItems(nextItems);
    setRules(nextRules);

    // Mirror immediately to localStorage so vault snapshot uses the latest data even if Send is clicked right away
    try {
      localStorage.setItem(`mudo:${FILE_ITEMS}`, JSON.stringify(nextItems));
      localStorage.setItem(`mudo:${FILE_RULES}`, JSON.stringify(nextRules));
    } catch {}

    // Flush immediately so Send/refresh cannot resurrect deleted items from stale data
    persistPackagingImmediate(nextItems, nextRules);

    notifyPackaging({
      id: `packaging-item-deleted-${id}`,
      message: "Item deleted",
      tone: "warn",
      level: "warning",
      actionTo: "/dashboard/packaging/library",
    });
  }, [persistPackagingImmediate, notifyPackaging]);

  /* ---- CRUD: rules ---- */
  const addRule = useCallback((rule) => {
    const next = normRule(rule);
    setRules((prev) => [next, ...prev]);
    notifyPackaging({
      id: `packaging-rule-added-${next.id}`,
      message: "Rule added",
      tone: "success",
      level: "success",
      actionTo: "/dashboard/packaging/rules",
    });
  }, [notifyPackaging]);

  const updateRule = useCallback((id, patch) => {
    setRules((prev) =>
      prev.map((r) =>
        String(r.id) === String(id)
          ? { ...normRule({ ...r, ...patch, id: r.id, createdAt: r.createdAt }), updatedAt: now() }
          : r
      )
    );
    notifyPackaging({
      id: `packaging-rule-updated-${id}`,
      message: "Rule updated",
      tone: "success",
      level: "success",
      actionTo: "/dashboard/packaging/rules",
    });
  }, [notifyPackaging]);

  const deleteRule = useCallback((id) => {
    setRules((prev) => prev.filter((r) => String(r.id) !== String(id)));
    notifyPackaging({
      id: `packaging-rule-deleted-${id}`,
      message: "Rule deleted",
      tone: "warn",
      level: "warning",
      actionTo: "/dashboard/packaging/rules",
    });
  }, [notifyPackaging]);

  /* ---- cost resolver ---- */
  const resolvePackagingUsage = useCallback(
    ({ productId = null, batchId = null, quantity = 1 }) => {
      const active = (rules || []).filter((r) => r.active);
      const scoped = [
        ...active.filter((r) => r.scope === "product" && r.productId === productId),
        ...active.filter((r) => r.scope === "batch" && r.batchId === batchId),
        ...active.filter((r) => r.scope === "global"),
      ];
      const itemMap = new Map((items || []).map((i) => [i.id, i]));
      const usage = [];

      for (const rule of scoped) {
        const it = itemMap.get(rule.itemId);
        if (!it) continue;

        let qty = 0;
        if (rule.appliesAs === "perUnit") {
          qty += Number(rule.qtyPerUnit || 0) * Number(quantity || 0);
        } else if (rule.appliesAs === "perBatch") {
          qty += Number(rule.qtyPerBatch || 0);
        } else if (rule.appliesAs === "threshold" && rule.threshold) {
          const { minQty = 0, step = 1, perStepQty = 0 } = rule.threshold;
          if (quantity >= minQty) {
            const steps = Math.ceil((quantity - minQty + 1) / step);
            qty += steps * Number(perStepQty || 0);
          }
        }
        if (qty <= 0) continue;

        const unitCost = pricePerUnit(it);
        const costTotal = unitCost * qty;
        usage.push({ item: it, rule, qtyTotal: qty, unitCost, costTotal });
      }

      const perBatchCost = usage
        .filter((u) => u.rule.appliesAs === "perBatch" || u.rule.appliesAs === "threshold")
        .reduce((a, b) => a + b.costTotal, 0);

      const perUnitCostRaw = usage
        .filter((u) => u.rule.appliesAs === "perUnit")
        .reduce((a, b) => a + b.costTotal, 0);

      const perUnitCost = quantity > 0 ? perUnitCostRaw / quantity : 0;
      const totalCost = perUnitCostRaw + perBatchCost;

      return { perUnitCost, perBatchCost, totalCost, breakdown: usage };
    },
    [rules, items]
  );

  /* ---- derived stats ---- */
  const totalItems = items.length;
  const activeRulesCount = rules.filter((r) => r.active).length;
  const byCategory = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.category || "Uncategorized";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map, ([name, count]) => ({ name, count }));
  }, [items]);
  const lastUpdated = useMemo(() => {
    const maxItem = Math.max(0, ...items.map((i) => Number(i.updatedAt || 0)));
    const maxRule = Math.max(0, ...rules.map((r) => Number(r.updatedAt || 0)));
    return Math.max(maxItem, maxRule);
  }, [items, rules]);

  const value = useMemo(
    () => ({
      items,
      rules,
      addItem,
      updateItem,
      deleteItem,
      addRule,
      updateRule,
      deleteRule,
      resolvePackagingUsage,
      totalItems,
      activeRulesCount,
      byCategory,
      lastUpdated,
    }),
    [
      items,
      rules,
      addItem,
      updateItem,
      deleteItem,
      addRule,
      updateRule,
      deleteRule,
      resolvePackagingUsage,
      totalItems,
      activeRulesCount,
      byCategory,
      lastUpdated,
    ]
  );

  return (
    <PackagingContext.Provider value={value}>
      {children}
    </PackagingContext.Provider>
  );
}

export const usePackaging = () => useContext(PackagingContext);
